var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { live } from 'lit/directives/live.js';
/**
 * A text field component that works with native browser forms.
 *
 * Uses ElementInternals for form association, allowing the component
 * to participate in form submission, validation, and reset.
 *
 * Native input and change events bubble up from the inner input element.
 */
export class KRTextField extends LitElement {
    constructor() {
        super();
        // The value the user last typed. While dirty, an external `value` set is reverted to this (see
        // willUpdate) so a parent re-binding from the server can't overwrite an unsaved edit.
        this._dirtyValue = '';
        /**
         * The input label text
         */
        this.label = '';
        /**
         * The input name for form submission
         */
        this.name = '';
        /**
         * The current value
         */
        this.value = '';
        /**
         * The value as a number. Kept in sync on input and on load; null when the value is empty or
         * non-numeric. <kr-form> reads this for number fields so the emitted change carries a real number
         * (or null to clear it).
         */
        this.valueAsNumber = null;
        /**
         * Placeholder text
         */
        this.placeholder = '';
        /**
         * Input type (text, email, password, tel, url, search)
         */
        this.type = 'text';
        /**
         * Whether the field is required
         */
        this.required = false;
        /**
         * Whether the field is disabled
         */
        this.disabled = false;
        /**
         * Whether the field is readonly
         */
        this.readonly = false;
        /**
         * Autocomplete attribute value
         */
        this.autocomplete = '';
        /**
         * Helper text shown below the input
         */
        this.hint = '';
        /**
         * Whether the user has edited this field since it was last loaded or marked pristine. Set on
         * user input, cleared by markPristine(). Used by <kr-form> to collect only changed fields.
         */
        this.dirty = false;
        /**
         * Whether a <kr-form> save carrying this field's edit is in flight. Set by markSaving(), cleared
         * by markSaved()/markSaveFailed().
         */
        this.saving = false;
        this._touched = false;
        this._handleInvalid = (e) => {
            e.preventDefault();
            this._touched = true;
        };
        this._internals = this.attachInternals();
    }
    connectedCallback() {
        super.connectedCallback();
        // Marks this as a krubble form field so <kr-form> can find its controls (.kr-form-field).
        this.classList.add('kr-form-field');
        this.addEventListener('invalid', this._handleInvalid);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('invalid', this._handleInvalid);
    }
    firstUpdated() {
        this._updateValidity();
    }
    willUpdate(changedProperties) {
        // This field's own save-mode, else the enclosing <kr-form>'s. Unset either way means manual.
        let saveMode = this.saveMode;
        if (!saveMode) {
            saveMode = this.closest('kr-form')?.saveMode;
        }
        // While the user has an unsaved edit, an external `value` set (e.g. a parent re-binding from a
        // server response that predates the edit) would overwrite it. Revert to the user's value. The
        // field accepts external values again once the edit is saved (markSaving clears dirty) or reset.
        if (changedProperties.has('value') && this.dirty && this.value !== this._dirtyValue && saveMode === 'autosave') {
            this.value = this._dirtyValue;
        }
    }
    updated(changedProperties) {
        if (changedProperties.has('required') || changedProperties.has('value')) {
            this._updateValidity();
        }
        // Keep valueAsNumber in sync with value (from user input or a programmatic set). Empty or
        // non-numeric is null.
        if (changedProperties.has('value')) {
            const parsed = Number(this.value);
            if (this.value === '' || Number.isNaN(parsed)) {
                this.valueAsNumber = null;
            }
            else {
                this.valueAsNumber = parsed;
            }
        }
    }
    // Form-associated custom element callbacks
    get form() {
        return this._internals.form;
    }
    get validity() {
        return this._internals.validity;
    }
    get validationMessage() {
        return this._internals.validationMessage;
    }
    get willValidate() {
        return this._internals.willValidate;
    }
    checkValidity() {
        return this._internals.checkValidity();
    }
    reportValidity() {
        return this._internals.reportValidity();
    }
    formResetCallback() {
        this.value = '';
        this._touched = false;
        this.dirty = false;
        this.saving = false;
        this._internals.setFormValue('');
        this._internals.setValidity({});
    }
    // Clears the dirty flag so the field is no longer reported as changed.
    markPristine() {
        this.dirty = false;
    }
    // <kr-form> calls this when a save carrying this field's edit starts. Clearing dirty here means a
    // further edit during the request is tracked as new and goes out in the next save.
    markSaving() {
        this.saving = true;
        this.dirty = false;
    }
    // <kr-form> calls this when the save succeeded. If the user edited mid-flight the field is dirty
    // again and stays that way.
    markSaved() {
        this.saving = false;
    }
    // <kr-form> calls this when the save failed. Restore dirty so the next change retries the edit.
    markSaveFailed() {
        this.saving = false;
        this.dirty = true;
        this._dirtyValue = this.value;
    }
    formStateRestoreCallback(state) {
        this.value = state;
    }
    // Public methods for programmatic control
    focus() {
        this._input?.focus();
    }
    blur() {
        this._input?.blur();
    }
    select() {
        this._input?.select();
    }
    _updateValidity() {
        if (this._input) {
            this._internals.setValidity(this._input.validity, this._input.validationMessage);
        }
    }
    _handleInput(e) {
        this.value = e.target.value;
        this.dirty = true;
        this._dirtyValue = this.value;
        this._internals.setFormValue(this.value);
        this._internals.setValidity(this._input.validity, this._input.validationMessage);
    }
    // The inner input's native `change` fires on commit (blur after an edit) but doesn't cross the
    // shadow boundary — re-dispatch a composed one so a wrapping <kr-form> hears it.
    _handleChange(e) {
        this.value = e.target.value;
        this._internals.setFormValue(this.value);
        this.dispatchEvent(new Event('change', {
            bubbles: true,
            composed: true,
        }));
    }
    _handleBlur() {
        this._touched = true;
        this._internals.setValidity(this._input.validity, this._input.validationMessage);
    }
    render() {
        let validationMessage = '';
        if (this._touched && this._input && !this._input.validity.valid) {
            validationMessage = this._input.validationMessage;
        }
        return html `
      <div class="wrapper">
        ${this.label
            ? html `
            <label for="input">
              ${this.label}
              ${this.required ? html `<span class="required" aria-hidden="true">*</span>` : ''}
            </label>
          `
            : ''}
        <input
          id="input"
          class=${classMap({
            'input--invalid': this._touched && this._input && !this._input.validity.valid
        })}
          type=${this.type}
          name=${this.name}
          .value=${live(this.value)}
          placeholder=${this.placeholder}
          ?required=${this.required}
          ?disabled=${this.disabled}
          ?readonly=${this.readonly}
          minlength=${ifDefined(this.minlength)}
          maxlength=${ifDefined(this.maxlength)}
          pattern=${ifDefined(this.pattern)}
          autocomplete=${ifDefined(this.autocomplete || undefined)}
          @input=${this._handleInput}
          @change=${this._handleChange}
          @blur=${this._handleBlur}
        />
        ${validationMessage
            ? html `<div class="validation-message">${validationMessage}</div>`
            : this.hint
                ? html `<div class="hint">${this.hint}</div>`
                : ''}
      </div>
    `;
    }
}
KRTextField.styles = css `
    :host {
      display: block;
      font-family: inherit;
    }

    .wrapper {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    label {
      font-size: 14px;
      font-weight: 500;
      color: black;
    }

    .required {
      color: var(--kr-text-field-required-color, #ef4444);
      margin-left: 0.125rem;
    }

    input {
      font-family: inherit;
      font-size: 14px;
      padding: 10px 12px;
      border: 1px solid var(--kr-text-field-border-color, #0000003d);
      border-radius: 8px;
      background-color: var(--kr-text-field-bg, #fff);
      color: var(--kr-text-field-color, #111827);
      transition: border-color 0.2s, box-shadow 0.2s;
    }

    input:focus {
      outline: none;
      border-color: var(--kr-text-field-focus-border-color, #163052);
      box-shadow: 0 0 0 3px var(--kr-text-field-focus-ring-color, rgba(22, 48, 82, 0.1));
    }

    input:disabled {
      background-color: var(--kr-text-field-disabled-bg, #f3f4f6);
      color: var(--kr-text-field-disabled-color, #9ca3af);
      cursor: not-allowed;
    }

    input::placeholder {
      color: var(--kr-text-field-placeholder-color, #9ca3af);
    }

    input.input--invalid {
      border-color: var(--kr-text-field-error-border-color, #ef4444);
    }

    input.input--invalid:focus {
      box-shadow: 0 0 0 3px var(--kr-text-field-error-ring-color, rgba(239, 68, 68, 0.1));
    }

    .validation-message {
      font-size: 0.75rem;
      color: var(--kr-text-field-error-color, #ef4444);
    }

    .hint {
      font-size: 0.75rem;
      color: var(--kr-text-field-helper-color, #6b7280);
    }
  `;
// Enable form association
KRTextField.formAssociated = true;
__decorate([
    property({ type: String })
], KRTextField.prototype, "label", void 0);
__decorate([
    property({ type: String })
], KRTextField.prototype, "name", void 0);
__decorate([
    property({ type: String })
], KRTextField.prototype, "value", void 0);
__decorate([
    property({ attribute: false })
], KRTextField.prototype, "valueAsNumber", void 0);
__decorate([
    property({ type: String })
], KRTextField.prototype, "placeholder", void 0);
__decorate([
    property({ type: String })
], KRTextField.prototype, "type", void 0);
__decorate([
    property({ type: Boolean })
], KRTextField.prototype, "required", void 0);
__decorate([
    property({ type: Boolean })
], KRTextField.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean })
], KRTextField.prototype, "readonly", void 0);
__decorate([
    property({ type: Number })
], KRTextField.prototype, "minlength", void 0);
__decorate([
    property({ type: Number })
], KRTextField.prototype, "maxlength", void 0);
__decorate([
    property({ type: String })
], KRTextField.prototype, "pattern", void 0);
__decorate([
    property({ type: String })
], KRTextField.prototype, "autocomplete", void 0);
__decorate([
    property({ type: String })
], KRTextField.prototype, "hint", void 0);
__decorate([
    property({
        type: Boolean,
        reflect: true,
    })
], KRTextField.prototype, "dirty", void 0);
__decorate([
    property({ attribute: 'save-mode' })
], KRTextField.prototype, "saveMode", void 0);
__decorate([
    property({
        type: Boolean,
        reflect: true,
    })
], KRTextField.prototype, "saving", void 0);
__decorate([
    state()
], KRTextField.prototype, "_touched", void 0);
__decorate([
    query('input')
], KRTextField.prototype, "_input", void 0);
// Guarded registration: no-op if the tag is already defined, so a second copy of krubble on
// the page (e.g. the Operations shell loads krubble globally while an embedded app bundled its
// own copy) doesn't throw. First definition wins.
if (!customElements.get('kr-text-field')) {
    customElements.define('kr-text-field', KRTextField);
}
//# sourceMappingURL=text-field.js.map